import { BehaviorSubject } from 'rxjs';
import { TransitionActionType } from '../lib/transition-action.model';

import * as Utils from '../lib/utils';
import { Workflow } from '../lib/workflow.model';

export class WorkflowAuditResult {
    public readonly issues: WorkflowAuditIssue[];

    public constructor(issues: WorkflowAuditIssue[] = []) {
        this.issues = issues;
    }

    public isValid(): boolean {
        return !this.hasErrors();
    }

    public isPerfect(): boolean {
      return this.isValid() && !this.hasWarnings() && !this.hasSuggestions();
    }

    public errors(): WorkflowAuditIssue[] { return this.issuesOfType('error'); }
    public warnings(): WorkflowAuditIssue[] { return this.issuesOfType('warning'); }
    public suggestions(): WorkflowAuditIssue[] { return this.issuesOfType('suggestion'); }
    private issuesOfType(type: WorkflowAuditIssueType): WorkflowAuditIssue[] {
        return this.issues.filter(i => i.type === type);
    }

    public hasErrors(): boolean { return this.hasIssuesOfType('error'); }
    public hasWarnings(): boolean { return this.hasIssuesOfType('warning'); }
    public hasSuggestions(): boolean { return this.hasIssuesOfType('suggestion'); }
    private hasIssuesOfType(type: WorkflowAuditIssueType): boolean {
        return this.issues.filter(i => i.type === type).length > 0;
    }
}

export type WorkflowAuditIssueType = 'error'|'warning'|'suggestion';
export class WorkflowAuditIssue {
  public readonly id: string;
  public readonly type: WorkflowAuditIssueType;
  public readonly description: string;

  constructor(type: WorkflowAuditIssueType, description: string) {
    this.id = Utils.randomString();
    this.type = type;
    this.description = description;
  }
}

export class WorkflowAuditService {

  private _result$$ = new BehaviorSubject<WorkflowAuditResult>(new WorkflowAuditResult());
  public get result$() { return this._result$$.asObservable(); }
  public get result(): WorkflowAuditResult { return this._result$$.value; }

  public constructor() {}

  public auditWorkflow(workflow: Workflow): WorkflowAuditResult {
    const issues: WorkflowAuditIssue[] = [];

    // Audit rules
    issues.push(...this.auditConfirmBookingAction(workflow));
    issues.push(...this.auditFixCapacity(workflow));
    issues.push(...this.auditFormNoInvalidFieldTypes(workflow));
    issues.push(...this.auditNoEmailSend(workflow));
    issues.push(...this.auditNoPaymentCollected(workflow));
    issues.push(...this.auditOrphanStates(workflow));

    // Update results
    const result = new WorkflowAuditResult(issues);
    this._result$$.next(result);
    return result;
  }

  public auditFormNoInvalidFieldTypes(workflow: Workflow): WorkflowAuditIssue[] {
    if (!workflow.form) { return []; }
    const issues: WorkflowAuditIssue[] =
      workflow.form.fields.reduce((issuesTemp, field) => {
        if (!field.type.isInvalid) { return issuesTemp; }
        const issue = new WorkflowAuditIssue('error',
                                             $localize `Invalid form field type '${field.type.name}' for '${field.name}' field`);
        issuesTemp.push(issue);
        return issuesTemp;
      }, [] as WorkflowAuditIssue[]);
    return issues;
  }

  public auditOrphanStates(workflow: Workflow): WorkflowAuditIssue[] {
    let orphans = [...workflow.states];
    workflow.transitions.forEach(t => {
      orphans = orphans.filter(s => s.id !== t.source.id && s.id !== t.target.id)
    });
    const issues: WorkflowAuditIssue[] = [];
    orphans.map(state => {
      const issue = new WorkflowAuditIssue('suggestion',
                                           $localize `State '${state.name}' is not connected through any transitions.`)
      issues.push(issue);
    });

    return issues;
  }

  public auditNoPaymentCollected(workflow: Workflow): WorkflowAuditIssue[] {
    if (workflow.transitions.length === 0) { return []; }

    const passed =
      workflow.transitions.reduce((result, transition) => {
        if (result) { return true; }
        return transition.actions.find(a => a.type === TransitionActionType.collectPayment) !== undefined;
      }, false);
    if (passed) { return []; }
    return [
      new WorkflowAuditIssue('suggestion',
                             $localize `No payment will be collected in this workflow`)
    ];
  }

  public auditConfirmBookingAction(workflow: Workflow): WorkflowAuditIssue[] {
    if (workflow.transitions.length === 0) { return []; }

    const passed =
      workflow.transitions.reduce((result, transition) => {
        if (result) { return true; }
        return transition.actions.find(a => a.type === TransitionActionType.confirmBooking) !== undefined;
      }, false);
    if (passed) { return []; }
    return [
      new WorkflowAuditIssue('suggestion',
                             $localize `Consider adding the 'confirm booking' action to the transition which completes`)
    ];
  }

  public auditFixCapacity(workflow: Workflow): WorkflowAuditIssue[] {
    if (workflow.transitions.length === 0) { return []; }

    const reserved =
      workflow.transitions.reduce((result, transition) => {
        if (result) { return true; }
        return transition.actions.find(a => a.type === TransitionActionType.reserveSeat) !== undefined;
      }, false);
    const confirm =
      workflow.transitions.reduce((result, transition) => {
        if (result) { return true; }
        return transition.actions.find(a => a.type === TransitionActionType.confirmSeat) !== undefined;
      }, false);
    if (confirm && reserved) { return []; }
    return [
      new WorkflowAuditIssue('suggestion',
                             $localize `Consider adding the 'reserve seat' and 'confirm seat' actions if your booking item features a fixed capacity`)
    ];
  }

  public auditNoEmailSend(workflow: Workflow): WorkflowAuditIssue[] {
    if (workflow.transitions.length === 0) { return []; }

    const passed =
      workflow.transitions.reduce((result, transition) => {
        if (result) { return true; }
        return transition.actions.find(a => a.type === TransitionActionType.sendEmail) !== undefined;
      }, false);
    if (passed) { return []; }
    return [
      new WorkflowAuditIssue('suggestion',
                             $localize `No emails will be sent out during this workflow`)
    ];
  }
}
